import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { keyBy, filter } from 'lodash'
import { useShellSessionsResources } from '../shared/resources/api/v1/admin/shell/useShellSessionsResources'

const ShellSessionsContext = createContext(null)

function ShellSessionsProvider({ children }) {
  const { teamSlug } = useParams()
  const { all, destroy } = useShellSessionsResources(teamSlug)
  const [sessions, setSessions] = useState([])
  const [currentSessionId, setCurrentSessionId] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const loadingRef = useRef(false)

  const loadSessions = useCallback(() => {
    if (loadingRef.current || !teamSlug) {
      return Promise.resolve([])
    }
    loadingRef.current = true
    setError(null)
    return all()
      .then((response) => {
        setSessions(response.shellSessions || [])
        return response.shellSessions || []
      })
      .catch((err) => {
        setError(err)
        throw err
      })
      .finally(() => {
        setLoading(false)
        loadingRef.current = false
      })
  }, [teamSlug, all])

  useEffect(() => {
    if (!teamSlug) {
      setSessions([])
      setCurrentSessionId(null)
      setLoading(false)
      return
    }

    setLoading(true)
    loadSessions()
  }, [teamSlug])

  const addSession = useCallback((session) => {
    setSessions((prev) => [...prev, session])
    setCurrentSessionId(session.id)
  }, [])

  const addPendingSession = useCallback((session) => {
    const pending = { ...session, pending: true }
    setSessions((prev) => [...prev, pending])
    setCurrentSessionId(pending.id)
    return pending
  }, [])

  const replacePendingSession = useCallback((pendingId, realSession) => {
    setSessions((prev) => prev.map((s) => s.id === pendingId ? realSession : s))
    setCurrentSessionId(realSession.id)
  }, [])

  const removeSession = useCallback((sessionId) => {
    setSessions((prev) => filter(prev, (s) => s.id !== sessionId))
    if (currentSessionId === sessionId) {
      setCurrentSessionId(null)
    }
  }, [currentSessionId])

  const sessionsById = useMemo(() => keyBy(sessions, 'id'), [sessions])

  const closeSession = useCallback((sessionId) => {
    const session = sessionsById[sessionId]
    if (session && session.pending) {
      removeSession(sessionId)
      return Promise.resolve()
    }
    return destroy(sessionId)
      .then(() => {
        removeSession(sessionId)
      })
  }, [destroy, removeSession, sessionsById])

  const selectSession = useCallback((sessionId) => {
    setCurrentSessionId(sessionId)
  }, [])

  const currentSession = useMemo(() => {
    return currentSessionId ? sessionsById[currentSessionId] : null
  }, [currentSessionId, sessionsById])

  const value = useMemo(() => ({
    sessions,
    sessionsById,
    currentSessionId,
    currentSession,
    loading,
    error,
    reload: loadSessions,
    addSession,
    addPendingSession,
    replacePendingSession,
    removeSession,
    closeSession,
    selectSession,
  }), [
    sessions, sessionsById, currentSessionId, currentSession,
    loading, error, loadSessions, addSession, addPendingSession,
    replacePendingSession, removeSession, closeSession, selectSession,
  ])

  return (
    <ShellSessionsContext.Provider value={value}>
      {children}
    </ShellSessionsContext.Provider>
  )
}

function useShellSessions() {
  const context = useContext(ShellSessionsContext)
  if (!context) {
    throw new Error('useShellSessions must be used within a ShellSessionsProvider')
  }
  return context
}

export { ShellSessionsProvider, useShellSessions }
